/**
 * O portão da rota `/financeiro`.
 *
 * A régua é a de `@/lib/financeiro/acesso`: super_admin sempre; administrador e
 * gerente de rede com ao menos uma unidade com o Financeiro ligado; o resto
 * nunca. Ela chega pelo mesmo valor que o menu lateral e o seletor do topo
 * usam (`useAcessoAoFinanceiro`), então menu e portão não discordam.
 *
 * O item do menu é cosmético. Este portão é a fronteira no cliente; no servidor
 * quem manda é a política das tabelas do Financeiro.
 *
 * 🚨 ENQUANTO A CHAVE DAS UNIDADES CARREGA, ESPERA. Mostrar "Acesso negado"
 * nesse meio-tempo faria a tela piscar a recusa para quem tem acesso.
 *
 * As frases são da tela; o motivo chega como código (`motivo`).
 */

import type { ReactNode } from "react"
import { Loader2 } from "lucide-react"
import { GlassButton } from "@/components/ui/GlassButton"
import { useAcessoAoFinanceiro } from "@/contexts/AcessoAoFinanceiroContext"

function Aviso({
  titulo,
  texto,
  acao,
}: {
  titulo: string
  texto: string
  acao?: { rotulo: string; onClick: () => void }
}) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="text-center space-y-2 max-w-md px-6">
        <p className="text-4xl" aria-hidden="true">
          🔒
        </p>
        <h2 className="text-xl font-semibold text-[var(--text-primary)]">{titulo}</h2>
        <p className="text-[var(--text-secondary)]">{texto}</p>
        {acao && (
          <div className="pt-3">
            <GlassButton variant="secondary" size="sm" onClick={acao.onClick}>
              {acao.rotulo}
            </GlassButton>
          </div>
        )}
      </div>
    </div>
  )
}

export function PortaoDoFinanceiro({ children }: { children: ReactNode }) {
  const acesso = useAcessoAoFinanceiro()

  if (acesso.estado === "carregando") {
    return (
      <div
        className="flex items-center gap-2 text-sm text-[var(--text-secondary)] py-10 justify-center"
        data-testid="financeiro-conferindo-acesso"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Abrindo…
      </div>
    )
  }

  if (acesso.estado === "falhou") {
    return (
      <Aviso
        titulo="Não foi possível abrir o Financeiro agora"
        texto="Verifique a conexão e tente de novo. Se continuar, fale com o suporte."
        acao={{ rotulo: "Tentar de novo", onClick: acesso.tentarDeNovo }}
      />
    )
  }

  if (!acesso.pode) {
    if (acesso.motivo === "sem_unidade_ligada") {
      return (
        <Aviso
          titulo="O Financeiro não está ligado nas suas unidades"
          texto="Para usar o Financeiro, peça à equipe da Invenia para ligá-lo na sua unidade."
        />
      )
    }
    return <Aviso titulo="Acesso negado" texto="Você não tem permissão para acessar esta área." />
  }

  return <>{children}</>
}
